// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Canonical regional population dataset for Finland.
 * Population values are read-only and must never be mutated in application state.
 */

export type PopulationMode = 'all' | 'adult_15_64';

export interface RegionStaticData {
  id: string;
  name: string;
  population_all: number;
  population_15_64: number;
}

const BASE_REGION_DATA: Array<Omit<RegionStaticData, 'population_15_64'>> = [
  { id: 'finland', name: 'Finland (entire country)', population_all: 5_636_000 },
  { id: 'lapland', name: 'Lapin hyvinvointialue', population_all: 180_000 },
  { id: 'pohjois-pohjanmaa', name: 'Pohjois-Pohjanmaan hyvinvointialue', population_all: 410_000 },
  { id: 'kainuu', name: 'Kainuun hyvinvointialue', population_all: 72_000 },
  { id: 'keski-pohjanmaa', name: 'Keski-Pohjanmaan hyvinvointialue', population_all: 68_000 },
  { id: 'keski-suomi', name: 'Keski-Suomen hyvinvointialue', population_all: 270_000 },
  { id: 'pohjois-savo', name: 'Pohjois-Savon hyvinvointialue', population_all: 250_000 },
  { id: 'pohjois-karjala', name: 'Pohjois-Karjalan hyvinvointialue', population_all: 160_000 },
  { id: 'etela-savo', name: 'Etelä-Savon hyvinvointialue', population_all: 130_000 },
  { id: 'etela-pohjanmaa', name: 'Etelä-Pohjanmaan hyvinvointialue', population_all: 190_000 },
  { id: 'pirkanmaa', name: 'Pirkanmaan hyvinvointialue', population_all: 520_000 },
  { id: 'kanta-hame', name: 'Kanta-Hämeen hyvinvointialue', population_all: 170_000 },
  { id: 'pohjanmaa', name: 'Pohjanmaan hyvinvointialue', population_all: 180_000 },
  { id: 'satakunta', name: 'Satakunnan hyvinvointialue', population_all: 220_000 },
  { id: 'varsinais-suomi', name: 'Varsinais-Suomen hyvinvointialue', population_all: 480_000 },
  { id: 'lansi-uusimaa', name: 'Länsi-Uudenmaan hyvinvointialue', population_all: 180_000 },
  { id: 'keski-uusimaa', name: 'Keski-Uudenmaan hyvinvointialue', population_all: 200_000 },
  { id: 'ita-uusimaa', name: 'Itä-Uudenmaan hyvinvointialue', population_all: 100_000 },
  { id: 'paijat-hame', name: 'Päijät-Hämeen hyvinvointialue', population_all: 200_000 },
  { id: 'kymenlaakso', name: 'Kymenlaakson hyvinvointialue', population_all: 170_000 },
  { id: 'etela-karjala', name: 'Etelä-Karjalan hyvinvointialue', population_all: 130_000 },
  { id: 'vantaa-kerava', name: 'Vantaan ja Keravan hyvinvointialue', population_all: 250_000 },
  { id: 'helsinki', name: 'Helsingin kaupunki', population_all: 670_000 },
  { id: 'ahvenanmaa', name: 'Ahvenanmaa (Ålands hälso- och sjukvård)', population_all: 30_000 },
];

const ADULT_SHARE = 3_501_000 / 5_636_000; // Align 15-64 population with Statistics Finland 2024

export const REGION_STATIC_DATA: RegionStaticData[] = BASE_REGION_DATA.map((region) => ({
  ...region,
  population_15_64: Math.round(region.population_all * ADULT_SHARE),
}));

export const REGION_LOOKUP: Record<string, RegionStaticData> = Object.fromEntries(
  REGION_STATIC_DATA.map((region) => [region.id, region])
);

export const normalizePopulationMode = (mode?: string): PopulationMode => {
  if (mode === 'adult_18_64') return 'adult_15_64';
  if (mode === 'adult_15_64') return 'adult_15_64';
  return 'all';
};

export function getRegionPopulation(id: string, mode: PopulationMode = 'all'): number {
  const region = REGION_LOOKUP[id];
  if (!region) return 0;
  const normalizedMode = normalizePopulationMode(mode);
  return normalizedMode === 'adult_15_64' ? region.population_15_64 : region.population_all;
}

export function getNationalPopulation(mode: PopulationMode = 'all'): number {
  return getRegionPopulation('finland', mode);
}


